// ============================================================================
// Was oben stehen bleibt, wenn die Seite rollt.
//
// ORIGINALTEXT MARVIN (03.09.2026, mit einem Bildschirmfoto der Konfiguration)
//   „hier als beispiel, auch bei den anderen seiten soll das menüband oben
//    stehen bleiben wenn die seite durchgescrollt wird. er soll den menüpunkt
//    als chip einfärben wenn man beim scrollen in dem bereich ist"
//
// DER KOPF KLEBT SEIT JEHER (`.kopf { position: sticky; top: 0 }`), DIE
// REITERLEISTE NICHT - und damit war sie nach dreissig Zeilen weg. Auf einem
// Bildschirm mit hundert Einstellungen heisst das: wer unten steht, weiss
// nicht mehr, in welchem Reiter er ist, und muss zum Wechseln erst wieder
// ganz nach oben rollen.
//
// WARUM DAS GEMESSEN WERDEN MUSS UND NICHT EINE ZAHL IM STYLESHEET IST.
// Eine klebende Leiste braucht ein `top`, und das ist die Höhe dessen, was
// ÜBER ihr klebt. Der Kopf ist aber nicht überall gleich hoch: in der
// Auftragsakte trägt er zwei Zeilen und eine Werkzeugleiste, auf dem Telefon
// bricht er um, und mit einem Übernahmebalken darüber verschiebt sich alles.
// Eine feste Zahl wäre an genau einem Bildschirm richtig - und an den anderen
// läge die Leiste entweder über dem Kopf oder es klaffte eine Lücke, durch
// die der Inhalt hindurchrollt.
//
// GEMESSEN WIRD DIE WIRKLICHE HÖHE, und zwar in zwei Zahlen:
//
//   --kopfhoehe    was über der Reiterleiste klebt (der Kopf).
//   --bandoben     dazu die Reiterleiste: dort klebt das Abschnittsband.
//   --klebehoehe   alles, was oben klebt, samt Leisten. Das ist der Abstand,
//                  den ein Sprungziel nach oben braucht (`scroll-margin-top`)
//                  - sonst landet der angesprungene Abschnitt UNTER der
//                  Leiste, und es sieht aus, als hätte der Sprung nichts
//                  getan.
//
// SIE STEHT OHNE JSX IN EINER EIGENEN DATEI, wie `namen.ts` und
// `terminraster.ts`: eine Datei mit JSX lässt sich mit
// `--experimental-strip-types` nicht laden, und dann bliebe einem Prüfstand
// nur die Textsuche - und die misst, dass etwas dasteht, nicht, dass es
// stimmt.
// ============================================================================

/* WAS OBEN KLEBT, IST GENAU EIN ELEMENT, UND DAS IST NACHGESEHEN UND NICHT
   ANGENOMMEN: `.kopf` trägt `position: sticky; top: 0`. Der Standbalken und
   der Übernahmebalken darüber rollen WEG - sie mitzuzählen hiesse, die
   Reiterleiste um ihre Höhe zu tief zu hängen, und dann klaffte nach dem
   ersten Rollen eine Lücke, durch die der Inhalt hindurchläuft. */
const KLEBER = ['.kopf'];

/* DIE LEISTEN, DIE UNTER DEM KOPF KLEBEN, in der Reihenfolge, in der sie
   sich stapeln. Genommen wird je Art die ERSTE: ein Fenster bringt seine
   eigene Reiterleiste mit (`Auftrag bearbeiten` hat sechs Reiter), und die
   klebt nicht am Kopf des Bildschirms - `stil.css` nimmt sie ausdrücklich
   aus. */
/* ZWEI SCHREIBWEISEN, EINE STUFE - UND SEIT DEM 08.09.2026 BELIEBIG VIELE
   LEISTEN DARIN. `.reiter` ist die unterstrichene Reiterleiste (Stammdaten,
   Akte, Verwaltung); `.reiterreihe` ist dieselbe Sache als Chipreihe über
   einer Liste (Aufmaß und Abrechnung, Posteingang, Kapazität, Arbeitsliste).

   ORIGINALTEXT MARVIN (03.09.2026, zu „Aufmaß und Abrechnung")
     „auch hier den oberenbereich ankleben, auch bei bei den anderen
      relevanten seiten direwkt mit umsetzen."

   **HIER STAND „UND KEINE SEITE TRÄGT BEIDE", UND DAS WAR EINE ANNAHME.**

   ORIGINALTEXT MARVIN (08.09.2026, mit dem Bildschirmfoto der Arbeitsliste)
     „die filter fehlen immernoch so wie wir sie kennen."

   ER HAT RECHT, UND SIE FEHLTEN NICHT - SIE LAGEN DARUNTER. Die Arbeitsliste
   trägt seit .131 ZWEI Chipreihen (der Zustand, und was ansteht), und beide
   bekamen dasselbe `top`. **Zwei klebende Zeilen stapeln sich nicht von
   selbst:** die zweite legte sich beim ersten Rollen über die erste, und auf
   dem Bildschirm blieb nur „Was ansteht" stehen. Der Satz stand seit .93
   wörtlich im Stylesheet und galt für das Abschnittsband - für die Leisten
   selbst hat ihn niemand angewendet.

   JEDE LEISTE BEKOMMT DESHALB IHR EIGENES `top` (`--eigenoben`), gemessen aus
   den Höhen der Leisten DAVOR. Das ist keine Sonderregel für diesen einen
   Bildschirm: es gilt für eine Leiste genauso wie für drei, und der nächste
   Bildschirm mit zwei Reihen erbt es, ohne dass jemand daran denkt. */
const LEISTEN = ['.reiter, .reiterreihe', '.abschnittsband'];

/**
 * Ein `top` an ein Element schreiben - und nur, wenn es sich geändert hat.
 *
 * Jeder Schreibvorgang weckt den MutationObserver, der diese Funktion gerufen
 * hat. Er sieht zwar keine Attribute (`childList`, `subtree`), aber die Regel
 * ist dieselbe wie bei der Tabellenbeschriftung: **wer bei jedem Durchgang
 * schreibt, schreibt irgendwann in einer Schleife.**
 */
function setzeOben(e: HTMLElement, px: number): void {
  const soll = `${px}px`;
  if (e.style.getPropertyValue('--eigenoben') !== soll) {
    e.style.setProperty('--eigenoben', soll);
  }
}

/**
 * Die Höhe der klebenden Zeilen einer Bühne ausrechnen.
 *
 * Gerechnet wird aus den ELEMENTEN, die wirklich dastehen, nicht aus einer
 * Liste von Zahlen: ein Bildschirm ohne Reiterleiste hat sie einfach nicht,
 * und ein Kopf, der auf dem Telefon umbricht, ist dort höher.
 */
export function klebehoehen(buehne: HTMLElement): {
  kopf: number; band: number; kleber: number;
} {
  let kopf = 0;
  for (const w of KLEBER) {
    /* Der ERSTE seiner Art in der Reihenfolge des Dokuments. Ein Fenster
       trägt seinen eigenen Kopf (`.dialogkopf`) und kommt hier nicht in die
       Quere; ein `:scope >` wäre trotzdem zu eng, denn nicht jeder
       Bildschirm legt seinen Kopf unmittelbar in die Bühne. */
    const e = buehne.querySelector<HTMLElement>(w);
    // `offsetHeight` und nicht `getBoundingClientRect().height`: ein Element
    // mit einer Übergangs-Animation liefert dort Zwischenwerte, und dann
    // springt die Leiste beim Zeichnen.
    if (e) kopf += e.offsetHeight;
  }
  // Was ÜBER dem Abschnittsband klebt, und was insgesamt oben klebt. Die
  // Reiterleiste zählt für beides, das Band nur für das zweite: es klebt ja
  // selbst dort, wo `--bandoben` endet.
  // Ein Fenster bringt seine eigene Reiterleiste mit, und die klebt nicht am
  // Kopf des Bildschirms. Sie mitzuzählen schöbe das Band um ihre Höhe nach
  // unten, solange das Fenster offen ist.
  const erste = (w: string) =>
    Array.from(buehne.querySelectorAll<HTMLElement>(w))
      .find((e) => !e.closest('.dialog')) ?? null;

  /* ALLE LEISTEN, NICHT NUR DIE ERSTE - und jede bekommt ihr eigenes `top`
     aus der Summe derer davor. Die Reihenfolge ist die des Dokuments, also
     die, in der sie auf dem Bildschirm untereinander stehen. */
  let band = kopf;
  for (const l of Array.from(buehne.querySelectorAll<HTMLElement>(LEISTEN[0]))
                       .filter((e) => !e.closest('.dialog'))) {
    setzeOben(l, band);
    band += l.offsetHeight;
  }
  let kleber = band;
  const streifen = erste(LEISTEN[1]);
  if (streifen) { setzeOben(streifen, kleber); kleber += streifen.offsetHeight; }
  return { kopf, band, kleber };
}

/**
 * Die Höhen an der Bühne festhalten und nachführen.
 *
 * Gibt die Funktion zurück, mit der man wieder aufhört. Zu beobachten sind
 * DREI Dinge, und jedes einzeln wäre zu wenig:
 *
 *   * die GRÖSSE der Bühne (das Fenster wird schmaler, der Kopf bricht um),
 *   * ihr INHALT (ein anderer Bildschirm bringt einen anderen Kopf mit),
 *   * und der erste Zeichendurchgang selbst.
 *
 * Gemessen wird höchstens einmal je Bild (`requestAnimationFrame`): eine
 * Messung je Änderung liefe bei einer Liste, die nachlädt, hundertmal.
 */
export function beobachteKlebehoehe(buehne: HTMLElement): () => void {
  let angefordert = 0;
  const messen = () => {
    angefordert = 0;
    const { kopf, band, kleber } = klebehoehen(buehne);
    buehne.style.setProperty('--kopfhoehe', `${kopf}px`);
    buehne.style.setProperty('--bandoben', `${band}px`);
    buehne.style.setProperty('--klebehoehe', `${kleber}px`);
  };
  const bald = () => {
    if (angefordert) return;
    angefordert = requestAnimationFrame(messen);
  };

  bald();
  const gr = new ResizeObserver(bald);
  gr.observe(buehne);
  const mu = new MutationObserver(bald);
  mu.observe(buehne, { childList: true, subtree: true });

  return () => {
    if (angefordert) cancelAnimationFrame(angefordert);
    gr.disconnect();
    mu.disconnect();
  };
}

/**
 * DIE HÖHE DER KOPFLEISTE, FÜR DAS AUSGEFAHRENE MENÜ.
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 * > „für das menü unten zb empfehle ich einen «burger» oben einzubauen."
 *
 * **DAS PANEL DARF DEN BURGER NICHT VERDECKEN**, sonst kommt man nicht mehr
 * zu: der Knopf, der es schliesst, läge darunter. Gefunden hat das kein
 * Nachdenken, sondern Prüfstand 017 beim ersten Lauf - er wollte den Burger
 * ein zweites Mal drücken und bekam „`<h4>Arbeit</h4>` intercepts pointer
 * events".
 *
 * Das Panel fängt deshalb UNTER der Kopfleiste an, und wie hoch die ist, wird
 * GEMESSEN und nicht gesetzt: sie trägt auf einem Telefon eine andere
 * Schriftgrösse als auf einem Tablet, und `env(safe-area-inset-top)` kommt je
 * nach Gerät dazu. Dieselbe Lehre wie bei der klebenden Reiterleiste darüber -
 * **eine feste Zahl wäre an genau einem Gerät richtig.**
 *
 * Die Zahl steht am WURZELELEMENT und nicht an der Bühne: das Panel ist ein
 * Geschwister der Bühne, und eine Variable, die an der Bühne hängt, erreicht
 * es nie.
 */
export function beobachteKopfleiste(): () => void {
  const kopf = document.querySelector<HTMLElement>('.kopfleiste');
  if (!kopf) return () => {};
  let angefordert = 0;
  const messen = () => {
    angefordert = 0;
    document.documentElement.style.setProperty(
      '--kopfleiste', `${Math.round(kopf.getBoundingClientRect().height)}px`);
  };
  const bald = () => {
    if (angefordert) return;
    angefordert = requestAnimationFrame(messen);
  };
  bald();
  const gr = new ResizeObserver(bald);
  gr.observe(kopf);
  return () => {
    if (angefordert) cancelAnimationFrame(angefordert);
    gr.disconnect();
  };
}
